import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { stateLabel } from "./Tournament.js";

/** The list of tournaments. Public: no sign-in needed. */
export function Tournaments() {
  const [tournaments, setTournaments] = useState<any[] | null>(null);

  useEffect(() => {
    void fetch("/api/tournaments")
      .then((r) => (r.ok ? r.json() : { tournaments: [] }))
      .then((d) => setTournaments(d.tournaments))
      .catch(() => setTournaments([]));
  }, []);

  if (!tournaments) return <p style={{ color: "#666" }}>Loading…</p>;

  if (tournaments.length === 0) {
    return (
      <div>
        <h1 style={{ marginTop: 0, fontSize: 24 }}>Tournaments</h1>
        <p style={{ color: "#888" }}>No tournaments are open at the moment.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ marginTop: 0, fontSize: 24 }}>Tournaments</h1>
      <div style={{ display: "grid", gap: 14, maxWidth: 720, marginTop: 20 }}>
        {tournaments.map((t) => (
          <Link
            key={t.slug}
            to={`/t/${t.slug}`}
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: 8,
              padding: 16,
              textDecoration: "none",
              color: "inherit",
              display: "block",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <strong style={{ fontSize: 16, color: "#00695c" }}>{t.name}</strong>
              <span
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 10,
                  background: t.state === "registration_open" ? "#e8f5e9" : "#eee",
                  color: t.state === "registration_open" ? "#2e7d32" : "#757575",
                  fontWeight: 600,
                }}
              >
                {stateLabel(t.state)}
              </span>
            </div>
            <div style={{ fontSize: 13.5, color: "#666", marginTop: 6 }}>
              {t.system === "swiss" ? `Swiss, ${t.rounds} rounds` : t.system} ·{" "}
              {t.timeControl.label} · {t.entrants}
              {t.maxPlayers ? ` / ${t.maxPlayers}` : ""} entered
              {when(t) && ` · ${when(t)}`}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const day: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
const dayTime: Intl.DateTimeFormatOptions = { ...day, hour: "2-digit", minute: "2-digit" };

/**
 * The date that matters for where the tournament is in its life: when it
 * starts, when it actually started, or when it was played.
 */
function when(t: {
  state: string;
  startsAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}): string | null {
  const fmt = (iso: string, o: Intl.DateTimeFormatOptions) =>
    new Date(iso).toLocaleString(undefined, o);

  if (t.state === "finished" || t.state === "abandoned") {
    const end = t.finishedAt ?? t.startedAt ?? t.startsAt;
    if (!end) return null;
    const start = t.startedAt ?? t.startsAt;
    const from = start ? fmt(start, day) : null;
    const to = fmt(end, day);
    const verb = t.state === "abandoned" ? "abandoned" : "played";
    return from && from !== to ? `${verb} ${from} – ${to}` : `${verb} ${to}`;
  }
  if (t.startedAt) return `started ${fmt(t.startedAt, dayTime)}`;
  if (t.startsAt) return `starts ${fmt(t.startsAt, dayTime)}`;
  return null;
}
